package er.prateek.crossly.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith

/** One level on a mode's map. [index] is 1-based within its tier. */
data class ModeLevelItem(val index: Int, val stars: Int, val locked: Boolean, val isNext: Boolean)

/**
 * The reusable tier-row + level-grid for the leveled math modes. Content-free: it renders only
 * from the counts/stars the caller passes, so it never materialises puzzles to draw the map. The
 * crossword's own `LevelSelectScreen` keeps its bespoke layout; this is the shared surface for the
 * new modes.
 */
@Composable
fun ModeLevelMap(
    tiers: List<DifficultyTier>,
    selectedTier: DifficultyTier,
    onSelectTier: (DifficultyTier) -> Unit,
    tierUnlocked: (DifficultyTier) -> Boolean,
    tierLabel: (DifficultyTier) -> String,
    tierProgress: (DifficultyTier) -> Pair<Int, Int>,
    levels: List<ModeLevelItem>,
    levelTitle: (Int) -> String,
    onOpenLevel: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = MaterialTheme.game
    Column(modifier = modifier.fillMaxSize(), verticalArrangement = Arrangement.spacedBy(Spacing.lg)) {
        Row(
            modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        ) {
            tiers.forEach { tier ->
                val (solved, total) = tierProgress(tier)
                TierChip(
                    tier = tier,
                    label = tierLabel(tier),
                    selected = tier == selectedTier,
                    unlocked = tierUnlocked(tier),
                    solved = solved,
                    total = total,
                    onClick = { onSelectTier(tier) },
                )
            }
        }

        val (solved, total) = tierProgress(selectedTier)
        val unlocked = tierUnlocked(selectedTier)
        GameCard(
            modifier = Modifier.fillMaxWidth(),
            accentGlow = if (unlocked) colors.tierColor(selectedTier).copy(alpha = 0.5f) else null,
        ) {
            if (unlocked) {
                LabeledProgress(
                    label = tierLabel(selectedTier),
                    value = "$solved / $total",
                    progress = if (total == 0) 0f else solved.toFloat() / total,
                    tint = colors.tierColor(selectedTier),
                )
            } else {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    IconLock(size = 18.dp, tint = colors.locked)
                    Text(
                        text = "Clear earlier levels to unlock",
                        style = MaterialTheme.typography.bodySmall,
                        color = colors.inkMuted,
                    )
                }
            }
        }

        LazyVerticalGrid(
            columns = GridCells.Adaptive(minSize = 104.dp),
            modifier = Modifier.fillMaxWidth().weight(1f),
            contentPadding = PaddingValues(vertical = Spacing.xs),
            horizontalArrangement = Arrangement.spacedBy(Spacing.md),
            verticalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            items(levels.size) { i ->
                val item = levels[i]
                Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
                    LevelTile(
                        number = item.index,
                        title = levelTitle(item.index),
                        stars = item.stars,
                        locked = item.locked,
                        tierColor = colors.tierColor(selectedTier),
                        isNext = item.isNext,
                        modifier = Modifier.widthIn(max = 128.dp),
                        onClick = { onOpenLevel(item.index) },
                    )
                }
            }
        }
    }
}

@Composable
private fun TierChip(
    tier: DifficultyTier,
    label: String,
    selected: Boolean,
    unlocked: Boolean,
    solved: Int,
    total: Int,
    onClick: () -> Unit,
) {
    val colors = MaterialTheme.game
    val tierColor = colors.tierColor(tier)
    val shape = RoundedCornerShape(16.dp)
    Row(
        modifier = Modifier
            .widthIn(min = 120.dp)
            .clip(shape)
            .background(if (selected) tierColor.mixWith(colors.card, 0.15f) else colors.card.copy(alpha = 0.6f))
            .border(
                width = if (selected) 2.dp else 1.5.dp,
                color = if (selected) tierColor else colors.cardBorder,
                shape = shape,
            )
            .clickable(onClick = onClick)
            .padding(horizontal = Spacing.lg, vertical = Spacing.md),
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(4.dp))
                .background(if (unlocked) tierColor else colors.locked)
                .width(4.dp)
                .padding(vertical = 10.dp),
        )
        Column {
            Text(
                text = label,
                style = MaterialTheme.typography.labelLarge,
                color = if (selected) colors.ink else colors.inkMuted,
            )
            Text(
                text = if (unlocked) "$solved / $total" else "Locked",
                style = MaterialTheme.typography.labelSmall,
                color = if (selected) colors.ink.copy(alpha = 0.8f) else colors.inkMuted,
            )
        }
        if (!unlocked) IconLock(size = 15.dp, tint = colors.locked)
    }
}
